import { useCallback } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import DoctorCard from "../../../components/dashboard/doctorCard/DoctorCard";
import InsuranceCard from "../../../components/dashboard/InsuranceCard/InsuranceCard";
import { getPercentageByTotal } from "../../../utilis/common";
import { HOSPITAL_CARDS_LABELS, HOSPITAL_CARDS_TYPE } from "../../../types/hospital.type";
import { PAGE_TYPE } from "../../../types/pages.type";
import DayCardList from "./dayCardList/DayCardList";
import DiagnosticsCardList from "./diagnosticsCardList/DiagnosticsCardList";
import DischargeEmergencyList from "./dischargeEmergencyList/DischargeEmergencyList";
import FloorCardList from "./floorCardList/FloorCardList";
import HospitalizationsList from "./hospitalizationsList/HospitalizationsList";
import NinetyDaysAnalysis from "./ninetyDaysAnalysis/NinetyDaysAnalysis";
import { HospitalList } from "../../admissions/components";
import ReturnCardList from "./returnCardList/ReturnCardList";
import ShiftCard from "./shiftCard/ShiftCard";
import DynamicDataCardList from "./DynamicDataCardList/DynamicDataCardList";

/** Match header filter keys to custom / dynamic tab definitions (same rules as useHeaderCustomLabelsItem). */
export function findHospitalDynamicCardByKey(dataKey, dynamicCards) {
	if (!dataKey || !Array.isArray(dynamicCards) || dynamicCards.length === 0) return null;

	const normalizeKey = (value = "") => String(value).trim().toLowerCase();
	const stripCustomTabSuffix = (value = "") => String(value).replace(/customtab$/i, "");
	const keyNorm = normalizeKey(dataKey);
	const keyNoSuffix = normalizeKey(stripCustomTabSuffix(dataKey));

	return (
		dynamicCards.find((item) => {
			const accessor = normalizeKey(item?.accessor);
			const accessorNoSuffix = normalizeKey(stripCustomTabSuffix(item?.accessor || ""));
			const itemKey = normalizeKey(item?.key);
			const itemId = normalizeKey(item?.id);
			const itemMongoId = normalizeKey(item?._id);

			return (
				accessor === keyNorm ||
				accessorNoSuffix === keyNoSuffix ||
				itemKey === keyNorm ||
				itemKey === keyNoSuffix ||
				itemId === keyNorm ||
				itemId === keyNoSuffix ||
				itemMongoId === keyNorm ||
				itemMongoId === keyNoSuffix
			);
		}) ?? null
	);
}

function HospitalHeaderDynamicFilterBody({
	ele,
	accessor,
	cardFilter,
	filterListData,
	filterListDataComparison,
	handleToggle,
	handleToggleAll,
	filter,
	filterComparison,
	loadingBoth,
	dbData,
	dbDataComparison,
	percentageAgainst,
	averageCensusComparison,
	projectionDays,
	getPriorityNumber,
	transferType,
	lockedTotalBy,
	filterTotal,
}) {
	const cardSelectedItems = cardFilter?.[accessor] ?? [];
	const cardID = filterListData?.[accessor]?.[0]?.id;
	const isSelectedTotalCard = cardSelectedItems.indexOf(cardID) !== -1;
	const isCustomTab = Boolean(ele?.isCustomTab);
	const question = ele?.question;

	const handleCardClick = useCallback(
		async (_titleParam = null, uniqueKey = null) => {
			const compositeKey = uniqueKey && accessor ? `${accessor}__${uniqueKey}` : null;
			const comparisonItem = filterListDataComparison?.[accessor]?.[0];
			await handleToggle({
				item: filterListData?.[accessor]?.[0],
				itemComparison: comparisonItem,
				type: accessor,
				isChecked: isSelectedTotalCard,
				cardTitle: ele.title || ele.label || ele.tableLabel,
				question: {
					isCustom: true,
					customQuestionInputType: question?.customQuestionInputType,
					isCustomTab: false,
					cardTitle: ele.title || ele.label || ele.tableLabel,
					customTab: ele.type,
					accessor,
					compositeKey,
					uniqueKey,
				},
				isCustomTab: false,
			});
		},
		[accessor, ele, filterListData, filterListDataComparison, handleToggle, isSelectedTotalCard, question]
	);

	return (
		<DynamicDataCardList
			compactForHeaderPopover
			disableInteractions={false}
			totalTransfers={dbData.total}
			totalTransfersComparison={dbDataComparison?.total}
			filterListData={filterListData}
			filterListDataComparison={filterListDataComparison}
			question={ele}
			cardTitle={ele.title || ele.label || ele.tableLabel}
			data={filterListData?.[accessor] ?? []}
			dataComparison={filterListDataComparison?.[accessor] ?? []}
			handleToggle={handleToggle}
			handleToggleAll={handleToggleAll}
			selectedItem={cardFilter?.[accessor]}
			spacialSelectedItem={cardFilter?.[`${accessor}_spacial`]}
			type={accessor}
			filter={filter}
			loading={loadingBoth}
			filterComparison={filterComparison}
			page={PAGE_TYPE.HOSPITAL}
			averageCensus={percentageAgainst}
			projectionDays={projectionDays}
			isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
			priorityNumber={getPriorityNumber(accessor)}
			transferType={transferType}
			lockedTotalBy={lockedTotalBy}
			isCustomTab={isCustomTab}
			filterTotal={filterTotal}
			handleCardClick={handleCardClick}
			isSelectedTotalCard={isSelectedTotalCard}
			cardFilter={cardFilter}
			isDisabled={false}
			admissionCompareAgainst={null}
			admissionCompareAgainstComparison={null}
			reverseColorsAdmissionPage={false}
			averageCensusComparison={averageCensusComparison}
		/>
	);
}

/**
 * Header popover shell aligned with dashboard {@link Card.module.scss}: white surface,
 * 8px radius, light shadow, and a title strip like {@code .cardHeaderContainer}.
 */
export function HeaderFilterCardShell({ title, children, bodySx = {} }) {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minWidth: { xs: 288, sm: 360 },
				maxWidth: 480,
				maxHeight: { xs: "min(72vh, 520px)", sm: 540 },
				bgcolor: "#fff",
				borderRadius: "8px",
				boxShadow:
					"0px 2px 4px rgba(15, 23, 42, 0.05), 0px 12px 32px -8px rgba(15, 23, 42, 0.16)",
				border: "2px solid rgba(15, 23, 42, 0.1)",
				overflow: "hidden",
			}}
		>
			<Box
				component="header"
				sx={{
					px: 1.75,
					py: 1.125,
					borderBottom: "1px solid #f6f6f9",
					flexShrink: 0,
					bgcolor: "#fff",
				}}
			>
				<Typography
					component="h2"
					variant="subtitle1"
					sx={{
						fontWeight: 700,
						fontSize: 15,
						lineHeight: 1.35,
						color: "#444652",
						fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
						letterSpacing: "-0.01em",
					}}
				>
					{title}
				</Typography>
			</Box>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					/* Avoid horizontal scrollbar from wide children (e.g. HospitalList selection nub) */
					minWidth: 0,
					overflowX: "hidden",
					overflowY: "auto",
					px: 1.25,
					py: 1.25,
					bgcolor: "#fff",
					"& .MuiListItem-root": { minHeight: 40 },
					...bodySx,
				}}
			>
				{children}
			</Box>
		</Box>
	);
}

export function PanelWrap({ title, children, contentSx = {} }) {
	return (
		<HeaderFilterCardShell title={title} bodySx={contentSx}>
			{children}
		</HeaderFilterCardShell>
	);
}

/**
 * Compact interactive filter UI for the hospital dashboard header popover.
 * Mirrors the inner body of each card on Hospital.js using the same handlers and data.
 */
export default function HospitalHeaderCardFilterPanel({
	dataKey,
	handleToggle,
	handleToggleAll,
	filterListData,
	filterListDataComparison,
	cardFilter,
	filter,
	filterComparison,
	loading,
	loadingComparison,
	projectionDays,
	filterTotal,
	filterTotalComparison,
	dbData,
	dbDataComparison,
	specialComparisonSet,
	transferType,
	lockedTotalBy,
	getPriorityNumber,
	comparingAgainstDifferentFacility,
	dynamicCards = [],
	hospitalDynamicTabDataByAccessor = {},
	percentageAgainst,
	/** When set, syncs vertical Collapse with header popover open state (Hospital page only). */
	headerFilterPopoverOpen,
}) {
	const loadingBoth = loading || loadingComparison;
	const averageCensus = specialComparisonSet ? filterTotal : getPercentageByTotal(dbData);
	const averageCensusComparison = specialComparisonSet ? filterTotalComparison : getPercentageByTotal(dbDataComparison);

	const panelContent = (() => {
		switch (dataKey) {
		case HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.hospitalizationsReHospitalizations}>
					<HospitalizationsList
						cardTitle="New Hospitalizations &amp; Re-Hospitalizations"
						handleToggle={handleToggle}
						selectedItem={cardFilter.hospitalizations}
						data={filterListData.hospitalizations}
						filter={filter}
						filterComparison={filterComparison}
						dataComparison={filterListDataComparison?.hospitalizations}
						page={PAGE_TYPE.HOSPITAL}
						loading={loadingBoth}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.DCER_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.DCERData} contentSx={{ pt: 1, pb: 0.75, px: 1.25 }}>
					<DischargeEmergencyList
						cardTitle="DC / ER"
						handleToggle={handleToggle}
						selectedItem={cardFilter.DCERData}
						data={filterListData.DCERData}
						loading={loadingBoth}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						dataComparison={filterListDataComparison?.DCERData}
						type={HOSPITAL_CARDS_TYPE.DCER_DATA}
						page={PAGE_TYPE.HOSPITAL}
						filter={filter}
						filterComparison={filterComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.DCER_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						compactForHeaderPopover
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.INSURANCE_DATA:
			return (
				<HeaderFilterCardShell
					title={HOSPITAL_CARDS_LABELS.insuranceData}
					bodySx={{ p: 0, pt: 0.5, overflow: "visible" }}
				>
					<InsuranceCard
						cardTitle={HOSPITAL_CARDS_LABELS.insuranceData}
						data={filterListData.insuranceData}
						dataComparison={filterListDataComparison?.insuranceData}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						selectedItem={cardFilter.insuranceData}
						type={HOSPITAL_CARDS_TYPE.INSURANCE_DATA}
						page={PAGE_TYPE.HOSPITAL}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						headerFilterPopover
						hideProgressBar
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.INSURANCE_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
					/>
				</HeaderFilterCardShell>
			);

		case HOSPITAL_CARDS_TYPE.RETURNS_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.returnsData}>
					<ReturnCardList
						cardTitle="Returned / Didn't Return"
						handleToggle={handleToggle}
						selectedItem={cardFilter.returnsData}
						transfers={specialComparisonSet ? filterTotal : filterTotal ? filterTotal : dbData.total}
						transfersComparison={
							specialComparisonSet ? filterTotalComparison : filterTotalComparison ? filterTotalComparison : dbDataComparison?.total
						}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						type={HOSPITAL_CARDS_TYPE.RETURNS_DATA}
						data={filterListData.returnsData}
						comparisonData={filterListDataComparison?.returnsData}
						page={PAGE_TYPE.HOSPITAL}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.RETURNS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						compactForHeaderPopover
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.ninetyDaysData}>
					<NinetyDaysAnalysis
						cardTitle="90 Days Analysis"
						handleToggle={handleToggle}
						loading={loadingBoth}
						selectedItem={cardFilter.ninetyDaysData}
						type={HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA}
						data={filterListData.ninetyDaysData}
						comparisonData={filterListDataComparison?.ninetyDaysData}
						page={PAGE_TYPE.HOSPITAL}
						filterListData={filterListData}
						filterListDataComparison={filterListDataComparison}
						filter={filter}
						filterComparison={filterComparison}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						hideRadialSection
						headerFilterPopover
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.FLOORS_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.floorsData}>
					<FloorCardList
						cardTitle={HOSPITAL_CARDS_LABELS.floorsData}
						data={filterListData.floorsData}
						dataComparison={filterListDataComparison?.floorsData}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						loading={loadingBoth}
						selectedItem={cardFilter.floorsData}
						type={HOSPITAL_CARDS_TYPE.FLOORS_DATA}
						page={PAGE_TYPE.HOSPITAL}
						filter={filter}
						filterComparison={filterComparison}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.FLOORS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.DOCTOR_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.doctorData} contentSx={{ px: 0, py: 0 }}>
					<DoctorCard
						cardTitle={HOSPITAL_CARDS_LABELS.doctorData}
						data={filterListData?.doctorData ?? []}
						comparisonData={filterListDataComparison?.doctorData}
						minimizable={true}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						selectedItem={cardFilter?.doctorData ?? []}
						type={HOSPITAL_CARDS_TYPE.DOCTOR_DATA}
						page={PAGE_TYPE.HOSPITAL}
						loading={loadingBoth}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						filter={filter}
						filterComparison={filterComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.DOCTOR_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
						forHeaderPopover
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.DAYS_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.daysData}>
					<DayCardList
						cardTitle={HOSPITAL_CARDS_LABELS.daysData}
						data={filterListData.daysData}
						dataComparison={filterListDataComparison?.daysData}
						handleToggle={handleToggle}
						selectedItem={cardFilter.daysData}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						type={HOSPITAL_CARDS_TYPE.DAYS_DATA}
						page={PAGE_TYPE.HOSPITAL}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.DAYS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.DX_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.dxData}>
					<DiagnosticsCardList
						cardTitle="Per Diagnosis"
						data={filterListData.dxData}
						dataComparison={filterListDataComparison?.dxData}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						selectedItem={cardFilter.dxData}
						type={HOSPITAL_CARDS_TYPE.DX_DATA}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						page={PAGE_TYPE.HOSPITAL}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.DX_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.SHIFT_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.shiftData}>
					<ShiftCard
						cardTitle="Per Shift"
						data={filterListData.shiftData}
						dataComparison={filterListDataComparison?.shiftData}
						handleToggle={handleToggle}
						selectedItem={cardFilter.shiftData}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						type={HOSPITAL_CARDS_TYPE.SHIFT_DATA}
						page={PAGE_TYPE.HOSPITAL}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						projectionDays={projectionDays}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.SHIFT_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.NURSE_DATA:
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.nurseData} contentSx={{ px: 0, py: 0 }}>
					<DoctorCard
						cardTitle="Per Nurse"
						data={filterListData?.nurseData ?? []}
						comparisonData={filterListDataComparison?.nurseData}
						minimizable={true}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						selectedItem={cardFilter?.nurseData ?? []}
						type={HOSPITAL_CARDS_TYPE.NURSE_DATA}
						page={PAGE_TYPE.HOSPITAL}
						loading={loadingBoth}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						filter={filter}
						filterComparison={filterComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.NURSE_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
						forHeaderPopover
					/>
				</PanelWrap>
			);

		case HOSPITAL_CARDS_TYPE.HOSPITAL_DATA: {
			const hasData = filterListData?.hospitalData?.length > 0;
			return (
				<PanelWrap title={HOSPITAL_CARDS_LABELS.hospitalData}>
					{hasData ? (
						<HospitalList
							dbData={dbData}
							dbDataComparison={dbDataComparison}
							cardTitle="Per Hospital"
							total={dbData.total}
							totalComparison={dbDataComparison?.total}
							data={filterListData.hospitalData}
							dataComparison={filterListDataComparison?.hospitalData}
							filter={filter}
							filterComparison={filterComparison}
							handleToggle={handleToggle}
							selectedItem={cardFilter.hospitalData}
							type={HOSPITAL_CARDS_TYPE.HOSPITAL_DATA}
							page={PAGE_TYPE.HOSPITAL}
							loading={loadingBoth}
							comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
							projectionDays={projectionDays}
							filterTotal={filterTotal}
							averageCensus={percentageAgainst ?? averageCensus}
							priorityData={cardFilter.mainPriorityData}
							priorityNumber={getPriorityNumber(HOSPITAL_CARDS_TYPE.HOSPITAL_DATA)}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
							disableInteractions={false}
							isMobile
						/>
					) : (
						<Typography variant="body2" sx={{ p: 1, color: "text.secondary", fontSize: 12 }}>
							No hospital destinations in this range.
						</Typography>
					)}
				</PanelWrap>
			);
		}

		default: {
			const dynamicEle =
				hospitalDynamicTabDataByAccessor[dataKey] ||
				findHospitalDynamicCardByKey(dataKey, dynamicCards);
			if (dynamicEle) {
				const accessor = dynamicEle.accessor || dataKey;
				const dynamicTitle =
					dynamicEle.title || dynamicEle.label || dynamicEle.tableLabel || String(dataKey);
				return (
					<PanelWrap title={dynamicTitle}>
						<HospitalHeaderDynamicFilterBody
							ele={dynamicEle}
							accessor={accessor}
							cardFilter={cardFilter}
							filterListData={filterListData}
							filterListDataComparison={filterListDataComparison}
							handleToggle={handleToggle}
							handleToggleAll={handleToggleAll}
							filter={filter}
							filterComparison={filterComparison}
							loadingBoth={loadingBoth}
							dbData={dbData}
							dbDataComparison={dbDataComparison}
							percentageAgainst={percentageAgainst ?? averageCensus}
							averageCensusComparison={averageCensusComparison}
							projectionDays={projectionDays}
							getPriorityNumber={getPriorityNumber}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							filterTotal={filterTotal}
						/>
					</PanelWrap>
				);
			}
			return (
				<PanelWrap title="Filter">
					<Typography variant="body2" sx={{ py: 0.5, color: "text.secondary", fontSize: 13, lineHeight: 1.5 }}>
						This filter is not available in the header preview.
					</Typography>
				</PanelWrap>
			);
		}
	}
	})();

	if (headerFilterPopoverOpen === undefined) {
		return panelContent;
	}

	return (
		<Collapse orientation="vertical" in={headerFilterPopoverOpen} timeout={1000} unmountOnExit={false}>
			{panelContent}
		</Collapse>
	);
}
